import { useCallback, useEffect, useRef, useState } from 'react';
import auth from '@client/components/auth';
import { Async } from '@client/new/helpers/types';
import {
  notifyCommunityUnreadChanged,
  useCommunityIndicator,
} from '@client/new/hooks/use-community-indicator';
import { CommunityId } from '../model/community.types';
import { Post, PostId } from '../model/post.types';

const SCROLL_TOP_THRESHOLD_PX = 16;

export type NewPostsIndicator = {
  hasNewPosts: boolean;
  onLoadNewPosts: () => void;
};

type SeenTopPost = { id: PostId; createdAtMs: number };

export const useNewPostsIndicator = (
  communityId: CommunityId,
  posts: Async<Post[]>,
): NewPostsIndicator => {
  const { setLastSeenPostId } = useCommunityIndicator();
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const seenTopPostRef = useRef<SeenTopPost | null>(null);

  const topPost =
    posts.type === 'fetched' && posts.value.length > 0 ? posts.value[0] : null;
  const topPostId = topPost === null ? null : topPost.id;
  const topPostIsOwn = topPost === null ? false : topPost.author.isOwn;
  const topPostCreatedAtMs =
    topPost === null ? null : topPost.createdAt.getTime();

  const acknowledgeTopPost = useCallback(() => {
    if (topPostId === null || topPostCreatedAtMs === null) return;
    seenTopPostRef.current = { id: topPostId, createdAtMs: topPostCreatedAtMs };
    setHasNewPosts(false);
    if (!auth.isAdministrator() && !auth.isModerator()) {
      setLastSeenPostId(topPostId);
    }
  }, [topPostId, topPostCreatedAtMs, setLastSeenPostId]);

  useEffect(() => {
    seenTopPostRef.current = null;
    setHasNewPosts(false);
  }, [communityId]);

  useEffect(() => {
    if (topPostId === null || topPostCreatedAtMs === null) return;
    const seen = seenTopPostRef.current;
    if (seen === null || topPostIsOwn) {
      acknowledgeTopPost();
      return;
    }
    if (topPostId === seen.id) {
      setHasNewPosts(false);
      return;
    }
    if (topPostCreatedAtMs > seen.createdAtMs) {
      if (window.scrollY <= SCROLL_TOP_THRESHOLD_PX) {
        acknowledgeTopPost();
        return;
      }
      setHasNewPosts(true);
      notifyCommunityUnreadChanged();
    }
  }, [topPostId, topPostIsOwn, topPostCreatedAtMs, acknowledgeTopPost]);

  const onLoadNewPosts = useCallback(() => {
    acknowledgeTopPost();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [acknowledgeTopPost]);

  useEffect(() => {
    if (!hasNewPosts) return;
    const handleWindowScroll = () => {
      if (window.scrollY <= SCROLL_TOP_THRESHOLD_PX) {
        acknowledgeTopPost();
      }
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [hasNewPosts, acknowledgeTopPost]);

  return { hasNewPosts, onLoadNewPosts };
};
